import { FC, useMemo } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { getAssessmentsGridColumns } from './academicsAssessments.helpers';
import {
  EAssessmentType,
  EAssessmentStatus,
  IAcademicsAssessmentClassMap,
  IAcademicsAssessment,
} from '../../../../../apis/academics/academicsAssessments.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useAcademicsAssessmentsContext } from '../../contexts/academicsAssessments.context';

interface FilterState {
  types: Set<EAssessmentType>;
  statuses: Set<EAssessmentStatus>;
  selectedClassId?: number;
  selectedGradeId?: string;
}

interface AcademicsAssessmentsGridProps {
  selectedItem: IAcademicsAssessmentClassMap | null;
  filters: FilterState;
  isLoading?: boolean;
}

interface AssessmentRow {
  id: string;
  name: string;
  assessmentType: EAssessmentType;
  status: EAssessmentStatus;
  maxScore: number;
  averageScore?: number;
  averagePercentage?: number;
  totalStudents?: number;
  scheduledDate?: string;
  conductedDate?: string;
  dueDate?: string;
  gradedDate?: string;
  weightage?: number;
}

const AcademicsAssessmentsGrid: FC<AcademicsAssessmentsGridProps> = ({
  selectedItem,
  filters,
  isLoading: propIsLoading,
}) => {
  const { translateText } = useTranslation();
  const { setSelectedAssessmentId } = useAcademicsAssessmentsContext();
  const assessmentsGridColumns = useMemo(
    () => getAssessmentsGridColumns(translateText),
    [translateText]
  );

  const assessmentRows = useMemo(() => {
    if (!selectedItem) return [];

    const allAssessments: IAcademicsAssessment[] = Object.values(
      selectedItem.assessments
    ).flat();

    let filteredAssessments = allAssessments;

    // Apply type filter
    if (filters.types.size > 0) {
      filteredAssessments = filteredAssessments.filter((assessment) =>
        filters.types.has(assessment.assessmentType)
      );
    }

    // Apply status filter
    if (filters.statuses.size > 0) {
      filteredAssessments = filteredAssessments.filter((assessment) =>
        filters.statuses.has(assessment.status)
      );
    }

    return filteredAssessments.map((assessment) => ({
      id: assessment.assessmentId,
      name: assessment.name,
      assessmentType: assessment.assessmentType,
      status: assessment.status,
      maxScore: assessment.maxScore,
      averageScore: assessment.averageScore,
      averagePercentage: assessment.averagePercentage,
      totalStudents: assessment.totalStudents,
      scheduledDate: assessment.scheduledDate,
      conductedDate: assessment.conductedDate,
      dueDate: assessment.dueDate,
      gradedDate: assessment.gradedDate,
      weightage: assessment.weightage,
    })) as AssessmentRow[];
  }, [selectedItem, filters]);

  if (!selectedItem) {
    return (
      <Grid container spacing={0}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              p: 4,
            }}
          >
            <Typography variant="h6" color="textSecondary">
              {translateText('academics.assessments.pleaseSelectClassAndGrade')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  const handleRowClick = (params: any) => {
    if (params?.row?.id) {
      setSelectedAssessmentId(params.row.id);
    }
  };

  return (
    <Grid container spacing={0}>
      <Grid
        item
        xs={12}
        sx={{
          height: { xs: 'calc(100vh - 400px)', sm: 'calc(100vh - 500px)' },
          overflow: 'auto',
        }}
      >
        <PaginatedGrid
          data-testid="academics-assessments-grid"
          rows={assessmentRows}
          columns={assessmentsGridColumns}
          loading={propIsLoading}
          onRowClick={handleRowClick}
          hideColumnsOnMobile={['weightage', 'conductedDate', 'dueDate']}
        />
      </Grid>
    </Grid>
  );
};

export default AcademicsAssessmentsGrid;
